import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import prodApi from '../../services/prodApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Package, Trash2, Pencil, FileText, AlertTriangle, Search, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const ESTADO_COLORS = {
  PENDIENTE: 'bg-orange-100 text-orange-700',
  PARCIAL: 'bg-blue-100 text-blue-700',
  COMPLETA: 'bg-emerald-100 text-emerald-700'
};

export const RecepcionesCompras = () => {
  const [recepciones, setRecepciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal editar
  const [editOpen, setEditOpen] = useState(false);
  const [editingRecepcion, setEditingRecepcion] = useState(null);
  const [editForm, setEditForm] = useState({
    costo_unitario: 0,
    proveedor: '',
    numero_documento: '',
    observaciones: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Modal eliminar
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingRecepcion, setDeletingRecepcion] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await prodApi.get('/inventario-ingresos?solo_recepciones=true');
      setRecepciones(res.data);
    } catch (error) {
      toast.error('Error al cargar recepciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (recepcion) => {
    setEditingRecepcion(recepcion);
    setEditForm({
      costo_unitario: recepcion.costo_unitario || 0,
      proveedor: recepcion.proveedor || '',
      numero_documento: recepcion.numero_documento || '',
      observaciones: recepcion.observaciones || ''
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingRecepcion) return;
    setSaving(true);
    try {
      await prodApi.put(`/inventario-ingresos/${editingRecepcion.id}`, editForm);
      toast.success('Recepción actualizada');
      setEditOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (recepcion) => {
    setDeletingRecepcion(recepcion);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingRecepcion) return;
    setDeleting(true);
    try {
      await prodApi.delete(`/inventario-ingresos/${deletingRecepcion.id}`);
      toast.success('Recepción eliminada. Stock y estado de factura actualizados.');
      setDeleteOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  // Filtrar recepciones
  const filteredRecepciones = recepciones.filter(r => {
    const term = searchTerm.toLowerCase();
    return (
      (r.item_nombre || '').toLowerCase().includes(term) ||
      (r.item_codigo || '').toLowerCase().includes(term) ||
      (r.compra_documento || '').toLowerCase().includes(term) ||
      (r.proveedor || '').toLowerCase().includes(term) ||
      (r.proveedor_nombre || '').toLowerCase().includes(term)
    );
  });

  // Totales
  const totalRecepciones = filteredRecepciones.length;
  const totalUnidades = filteredRecepciones.reduce((sum, r) => sum + (r.cantidad || 0), 0);
  const totalValor = filteredRecepciones.reduce((sum, r) => sum + ((r.cantidad || 0) * (r.costo_unitario || 0)), 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-PE');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Package className="w-8 h-8 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Recepciones de Compras</h1>
            <p className="text-sm text-gray-500">
              Ingresos de inventario vinculados a facturas de proveedor
            </p>
          </div>
        </div>
        
        {/* Stats */}
        <div className="flex gap-4 text-sm">
          <div className="bg-emerald-50 px-3 py-2 rounded-lg">
            <span className="text-gray-600">Total: </span>
            <span className="font-bold text-emerald-700">{totalRecepciones}</span>
          </div>
          <div className="bg-blue-50 px-3 py-2 rounded-lg">
            <span className="text-gray-600">Unidades: </span>
            <span className="font-bold text-blue-700">{totalUnidades.toLocaleString()}</span>
          </div>
          <div className="bg-purple-50 px-3 py-2 rounded-lg">
            <span className="text-gray-600">Valor: </span>
            <span className="font-bold text-purple-700">S/ {totalValor.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Buscar por artículo, código, factura o proveedor..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : filteredRecepciones.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {searchTerm ? 'No se encontraron recepciones' : 'No hay recepciones de compras registradas'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Factura</TableHead>
                  <TableHead>Proveedor</TableHead>
                  <TableHead>Artículo</TableHead>
                  <TableHead className="text-right">Cantidad</TableHead>
                  <TableHead className="text-right">Costo Unit.</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Estado Factura</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecepciones.map((recepcion) => {
                  const canDelete = recepcion.cantidad === recepcion.cantidad_disponible;
                  const total = (recepcion.cantidad || 0) * (recepcion.costo_unitario || 0);
                  
                  return (
                    <TableRow key={recepcion.id}>
                      <TableCell>{formatDate(recepcion.fecha)}</TableCell>
                      <TableCell>
                        {recepcion.compra_id ? (
                          <Link 
                            to={`/finanzas/compras/${recepcion.compra_id}/editar?tipo=FACTURA_PROVEEDOR`}
                            className="flex items-center gap-1 font-medium text-blue-600 hover:text-blue-800 hover:underline"
                            title="Ver factura"
                          >
                            <FileText className="w-4 h-4" />
                            {recepcion.compra_documento || '-'}
                            <ExternalLink className="w-3 h-3 opacity-50" />
                          </Link>
                        ) : (
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{recepcion.compra_documento || '-'}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{recepcion.proveedor_nombre || recepcion.proveedor || '-'}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{recepcion.item_nombre}</p>
                          {recepcion.item_codigo && (
                            <p className="text-xs text-gray-500">{recepcion.item_codigo}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">{recepcion.cantidad}</TableCell>
                      <TableCell className="text-right">
                        S/ {(recepcion.costo_unitario || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-medium text-emerald-600">
                        S/ {total.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell>
                        {recepcion.estado_recepcion && (
                          <Badge className={ESTADO_COLORS[recepcion.estado_recepcion] || 'bg-gray-100'}>
                            {recepcion.estado_recepcion}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(recepcion)}
                            title="Editar"
                          >
                            <Pencil className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(recepcion)}
                            title={canDelete ? 'Eliminar' : 'No se puede eliminar (ya tiene salidas)'}
                            disabled={!canDelete}
                          >
                            <Trash2 className={`w-4 h-4 ${canDelete ? 'text-red-500' : 'text-gray-300'}`} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal Editar */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-600" />
              Editar Recepción
            </DialogTitle>
          </DialogHeader>
          
          {editingRecepcion && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium">{editingRecepcion.item_nombre}</p>
                <p className="text-sm text-gray-500">
                  Cantidad: {editingRecepcion.cantidad} | Factura: {editingRecepcion.compra_documento || '-'}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Costo Unitario</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.costo_unitario}
                    onChange={(e) => setEditForm({...editForm, costo_unitario: parseFloat(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Proveedor</Label>
                  <Input
                    value={editForm.proveedor}
                    onChange={(e) => setEditForm({...editForm, proveedor: e.target.value})}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Número de Documento</Label>
                  <Input
                    value={editForm.numero_documento}
                    onChange={(e) => setEditForm({...editForm, numero_documento: e.target.value})}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Observaciones</Label>
                  <Textarea
                    value={editForm.observaciones}
                    onChange={(e) => setEditForm({...editForm, observaciones: e.target.value})}
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Eliminar */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Eliminar Recepción
            </DialogTitle>
          </DialogHeader>
          
          {deletingRecepcion && (
            <div className="space-y-4">
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="font-medium text-red-800">{deletingRecepcion.item_nombre}</p>
                <p className="text-sm text-red-700">
                  Cantidad: {deletingRecepcion.cantidad} | Factura: {deletingRecepcion.compra_documento || '-'}
                </p>
              </div>

              <div className="text-sm text-gray-600 space-y-2">
                <p>Al eliminar esta recepción:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-500">
                  <li>Se restará <strong>{deletingRecepcion.cantidad}</strong> unidades del stock</li>
                  <li>Se actualizará el estado de recepción de la factura</li>
                  <li>Esta acción no se puede deshacer</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={confirmDelete} 
              disabled={deleting} 
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deleting ? 'Eliminando...' : 'Sí, Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecepcionesCompras;
